import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle, ChevronDown, ChevronRight, Edit, Lock, Users, Search, RefreshCw, Filter, TrendingUp, DollarSign, CheckCircle2, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import EditPolicyDialog from "@/components/portal/EditPolicyDialog";
import SplitsManager from "@/components/portal/SplitsManager";
import DateRangePicker from "@/components/portal/DateRangePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert unix timestamp to Date
const convertUnixToDate = (timestamp) => {
  if (!timestamp) return null;
  // Check if timestamp is in seconds (less than 10 digits) or milliseconds
  const dateInMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  return new Date(dateInMs);
};

// Helper function to format date for display
const formatDisplayDate = (timestamp) => {
  const date = convertUnixToDate(timestamp);
  if (!date) return 'N/A';
  try {
    return format(date, 'MMM dd, yyyy');
  } catch {
    return 'N/A';
  }
};

// Helper function to format date from YYYY-MM-DD string (avoids timezone issues)
const formatEffectiveDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    // Parse date components manually to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return format(date, 'MMM dd, yyyy');
  } catch {
    return 'N/A';
  }
};

export default function MyPolicies({ userInfo }) {
  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedSplits, setExpandedSplits] = useState({});
  const [expandedText, setExpandedText] = useState({});
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Delete confirmation modal
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState([]);
  const [paidStatusFilter, setPaidStatusFilter] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState('created'); // 'created' or 'effective'
  const [searchQuery, setSearchQuery] = useState('');
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [uniquePaidStatuses, setUniquePaidStatuses] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalPolicies: 0,
    totalAnnualPremium: 0,
    approved: { count: 0, premium: 0 },
    underwriting: { count: 0, premium: 0 },
    followUp: { count: 0, premium: 0 },
    cancelledBeforeDraft: { count: 0, premium: 0 },
    declined: { count: 0, premium: 0 },
    lapsedPending: { count: 0, premium: 0 },
    lapsed: { count: 0, premium: 0 },
    notTaken: { count: 0, premium: 0 }
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API}/portal/my-policies`, {
        params: { agent_id: userInfo.agent_id }
      });
      setPolicies(response.data);
      setFilteredPolicies(response.data);
      
      // Calculate unique statuses
      const statuses = [...new Set(response.data.map(p => p.policyStatus).filter(Boolean))];
      setUniqueStatuses(statuses);
      
      // Calculate unique paid statuses
      const paidStatuses = [...new Set(response.data.map(p => p.paid_status).filter(Boolean))];
      setUniquePaidStatuses(paidStatuses);
      
      // Calculate dashboard stats
      const stats = calculateDashboardStats(response.data);
      setDashboardStats(stats);
      
    } catch (err) {
      console.error('Error fetching policies:', err);
      setError('Failed to load policies. Please try again.');
      toast.error('Failed to load policies. Please try again.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const calculateDashboardStats = (policiesData) => {
    let totalPolicies = policiesData.length;
    let totalAnnualPremium = 0;
    let approved = { count: 0, premium: 0 };
    let underwriting = { count: 0, premium: 0 };
    let followUp = { count: 0, premium: 0 };
    let cancelledBeforeDraft = { count: 0, premium: 0 };
    let declined = { count: 0, premium: 0 };
    let lapsedPending = { count: 0, premium: 0 };
    let lapsed = { count: 0, premium: 0 };
    let notTaken = { count: 0, premium: 0 };

    policiesData.forEach(policy => {
      const premium = parseFloat(policy.annual_premium) || 0;
      totalAnnualPremium += premium;
      
      const status = (policy.policyStatus || '').toLowerCase();
      
      if (status.includes('approved') || status.includes('active')) {
        approved.count++;
        approved.premium += premium;
      } else if (status.includes('underwriting')) {
        underwriting.count++;
        underwriting.premium += premium;
      } else if (status.includes('follow')) {
        followUp.count++;
        followUp.premium += premium;
      } else if (status.includes('cancelled before draft')) {
        cancelledBeforeDraft.count++;
        cancelledBeforeDraft.premium += premium;
      } else if (status.includes('declined')) {
        declined.count++;
        declined.premium += premium;
      } else if (status.includes('lapsed pending')) {
        lapsedPending.count++;
        lapsedPending.premium += premium;
      } else if (status === 'lapsed') {
        lapsed.count++;
        lapsed.premium += premium;
      } else if (status.includes('not taken')) {
        notTaken.count++;
        notTaken.premium += premium;
      }
    });

    return { 
      totalPolicies, 
      totalAnnualPremium, 
      approved, 
      underwriting, 
      followUp, 
      cancelledBeforeDraft,
      declined,
      lapsedPending,
      lapsed,
      notTaken
    };
  };

  useEffect(() => {
    applyFilters();
  }, [policies, statusFilter, paidStatusFilter, dateRange, dateFilterType, searchQuery]);

  // Handler for dashboard card clicks to filter
  const handleDashboardFilter = (filterType) => {
    switch(filterType) {
      case 'total':
        // Clear all filters to show all policies
        setStatusFilter([]);
        setPaidStatusFilter([]);
        setDateRange({ from: '', to: '' });
        setSearchQuery('');
        break;
      case 'approved':
        // Filter to approved/active policies
        const approvedStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('approved') || s.toLowerCase().includes('active')
        );
        setStatusFilter(approvedStatuses);
        break;
      case 'underwriting':
        // Filter to underwriting policies
        const underwritingStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('underwriting')
        );
        setStatusFilter(underwritingStatuses);
        break;
      case 'cancelled':
        // Filter to cancelled before draft
        const cancelledStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('cancelled before draft')
        );
        setStatusFilter(cancelledStatuses);
        break;
      case 'declined':
        // Filter to declined
        const declinedStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('declined')
        );
        setStatusFilter(declinedStatuses);
        break;
      case 'lapsedPending':
        // Filter to lapsed pending
        const lapsedPendingStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('lapsed pending')
        );
        setStatusFilter(lapsedPendingStatuses);
        break;
      case 'lapsed':
        // Filter to lapsed (but not lapsed pending)
        const lapsedStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase() === 'lapsed'
        );
        setStatusFilter(lapsedStatuses);
        break;
      case 'notTaken':
        // Filter to not taken
        const notTakenStatuses = uniqueStatuses.filter(s => 
          s.toLowerCase().includes('not taken')
        );
        setStatusFilter(notTakenStatuses);
        break;
      default:
        break;
    }
  };

  const applyFilters = () => {
    let filtered = [...policies];

    // Status filter (multi-select)
    if (statusFilter && statusFilter.length > 0) {
      filtered = filtered.filter(policy => statusFilter.includes(policy.policyStatus));
    }

    // Paid status filter (multi-select)
    if (paidStatusFilter && paidStatusFilter.length > 0) {
      filtered = filtered.filter(policy => paidStatusFilter.includes(policy.paid_status));
    }

    // Date range filter (by created_at or initial_draft_date based on dateFilterType)
    if (dateRange && dateRange.from && dateRange.to) {
      filtered = filtered.filter(policy => {
        let policyDate;
        
        if (dateFilterType === 'effective') {
          // Use initial_draft_date for effective date
          if (!policy.initial_draft_date) return false;
          const dateStr = policy.initial_draft_date;
          const [year, month, day] = dateStr.split('-').map(Number);
          policyDate = new Date(year, month - 1, day);
        } else {
          // Use created_at for created date
          policyDate = convertUnixToDate(policy.created_at);
          if (!policyDate) return false;
        }
        
        // Set hours for proper comparison
        policyDate.setHours(0, 0, 0, 0);
        const startDate = new Date(dateRange.from);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(dateRange.to);
        endDate.setHours(23, 59, 59, 999);
        
        return policyDate >= startDate && policyDate <= endDate;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(policy =>
        policy.client?.toLowerCase().includes(query) ||
        policy.policy_number?.toLowerCase().includes(query) ||
        policy.policyCarrier?.toLowerCase().includes(query) ||
        policy.phone?.toLowerCase().includes(query)
      );
    }

    setFilteredPolicies(filtered);
    setCurrentPage(1);
  };

  // Recalculate dashboard stats based on filtered policies
  const filteredDashboardStats = useMemo(() => {
    return calculateDashboardStats(filteredPolicies);
  }, [filteredPolicies]);

  const toggleRow = (policyId) => {
    setExpandedRows(prev => ({
      ...prev,
      [policyId]: !prev[policyId]
    }));
  };

  const toggleSplits = (policyId) => {
    setExpandedSplits(prev => ({
      ...prev,
      [policyId]: !prev[policyId]
    }));
  };

  const toggleText = (key) => {
    setExpandedText(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setShowEditDialog(true);
  };

  const handleDelete = (policy) => {
    setDeletingPolicy(policy);
    setDeleteReason('');
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    // Validate that reason is provided
    if (!deleteReason.trim()) {
      toast.error('Please provide a reason for deleting this policy');
      return;
    }

    try {
      setDeleting(true);
      
      const deletePayload = {
        policy_id: deletingPolicy.id,
        reason: deleteReason.trim(),
        user_id: userInfo.user_id
      };
      
      await axios.post(
        `${API}/portal/myPolicies/delete`,
        deletePayload
      );
      
      toast.success('Policy deleted successfully');
      setShowDeleteConfirmModal(false);
      setDeletingPolicy(null);
      setDeleteReason('');
      fetchPolicies(); // Refresh the list
    } catch (err) {
      console.error('Error deleting policy:', err);
      toast.error(err.response?.data?.message || 'Failed to delete policy');
    } finally {
      setDeleting(false);
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatSplit = (primary, fallback) => {
    if (primary && primary !== 'na') return primary;
    if (fallback && fallback !== 'na') return fallback;
    return 'N/A';
  };

  // Format fields with "Other" handling
  const formatWithOther = (mainValue, customValue) => {
    if (!mainValue || mainValue === 'na') return 'N/A';
    if (mainValue.toLowerCase() === 'other' && customValue && customValue !== 'na') {
      return `Other - ${customValue}`;
    }
    return mainValue;
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPolicies = filteredPolicies.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-neutral-600">{error}</p>
        <Button onClick={fetchPolicies} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold text-neutral-800">My Policies</CardTitle>
            <Button
              onClick={fetchPolicies}
              variant="outline"
              size="sm"
              className="rounded-lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Dashboard Stats - Modern Design */}
          <div className="grid grid-cols-4 gap-6 mt-6">
            {/* Total Policies - Hero Card */}
            <button 
              onClick={() => handleDashboardFilter('total')}
              className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20 cursor-pointer hover:scale-105 text-left"
            >
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">Total Policies</p>
                  <TrendingUp className="w-5 h-5 text-[#b8941e]" />
                </div>
                <p className="text-4xl font-black text-[#b8941e] mb-1">{filteredDashboardStats.totalPolicies}</p>
                <p className="text-xs text-neutral-600 font-medium">Click to show all</p>
              </div>
              <div className="absolute -right-4 -bottom-4 opacity-10">
                <div className="w-24 h-24 bg-[#d4af37] rounded-full"></div>
              </div>
            </button>

            {/* Approved */}
            <button 
              onClick={() => handleDashboardFilter('approved')}
              className="relative overflow-hidden bg-gradient-to-br from-emerald-400 via-green-300 to-green-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 text-left"
            >
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Approved</p>
                  <CheckCircle2 className="w-5 h-5 text-green-700" />
                </div>
                <p className="text-3xl font-black text-green-900 mb-1">{filteredDashboardStats.approved.count}</p>
                <p className="text-sm font-bold text-green-700">${filteredDashboardStats.approved.premium.toLocaleString()}</p>
              </div>
              <div className="absolute -right-6 -bottom-6 opacity-10">
                <CheckCircle2 className="w-32 h-32 text-green-900" />
              </div>
            </button>

            {/* Underwriting */}
            <button 
              onClick={() => handleDashboardFilter('underwriting')}
              className="relative overflow-hidden bg-gradient-to-br from-blue-400 via-blue-300 to-blue-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 text-left"
            >
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">Underwriting</p>
                  <Clock className="w-5 h-5 text-blue-700" />
                </div>
                <p className="text-3xl font-black text-blue-900 mb-1">{filteredDashboardStats.underwriting.count}</p>
                <p className="text-sm font-bold text-blue-700">${filteredDashboardStats.underwriting.premium.toLocaleString()}</p>
              </div>
              <div className="absolute -right-6 -bottom-6 opacity-10">
                <Clock className="w-32 h-32 text-blue-900" />
              </div>
            </button>

            {/* Total Premium */}
            <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Total Premium</p>
                  <DollarSign className="w-5 h-5 text-amber-700" />
                </div>
                <p className="text-2xl font-black text-amber-900 mb-1">${filteredDashboardStats.totalAnnualPremium.toLocaleString()}</p>
                <p className="text-xs text-amber-700 font-medium">Annual Revenue</p>
              </div>
              <div className="absolute -right-6 -bottom-6 opacity-10">
                <DollarSign className="w-32 h-32 text-amber-900" />
              </div>
            </div>
          </div>

          {/* Dashboard Stats - Row 2 - Compact Status Cards */}
          <div className="grid grid-cols-5 gap-4 mt-4">
            <button 
              onClick={() => handleDashboardFilter('cancelled')}
              className="bg-white border-2 border-red-200 p-4 rounded-xl hover:border-red-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
            >
              <p className="text-xs font-semibold text-red-600 mb-1">Cancelled</p>
              <p className="text-2xl font-bold text-red-700">{filteredDashboardStats.cancelledBeforeDraft.count}</p>
              <p className="text-xs text-red-500 font-medium">${filteredDashboardStats.cancelledBeforeDraft.premium.toLocaleString()}</p>
            </button>
            <button 
              onClick={() => handleDashboardFilter('declined')}
              className="bg-white border-2 border-orange-200 p-4 rounded-xl hover:border-orange-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
            >
              <p className="text-xs font-semibold text-orange-600 mb-1">Declined</p>
              <p className="text-2xl font-bold text-orange-700">{filteredDashboardStats.declined.count}</p>
              <p className="text-xs text-orange-500 font-medium">${filteredDashboardStats.declined.premium.toLocaleString()}</p>
            </button>
            <button 
              onClick={() => handleDashboardFilter('lapsedPending')}
              className="bg-white border-2 border-yellow-200 p-4 rounded-xl hover:border-yellow-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
            >
              <p className="text-xs font-semibold text-yellow-600 mb-1">Lapsed Pending</p>
              <p className="text-2xl font-bold text-yellow-700">{filteredDashboardStats.lapsedPending.count}</p>
              <p className="text-xs text-yellow-600 font-medium">${filteredDashboardStats.lapsedPending.premium.toLocaleString()}</p>
            </button>
            <button 
              onClick={() => handleDashboardFilter('lapsed')}
              className="bg-white border-2 border-purple-200 p-4 rounded-xl hover:border-purple-400 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
            >
              <p className="text-xs font-semibold text-purple-600 mb-1">Lapsed</p>
              <p className="text-2xl font-bold text-purple-700">{filteredDashboardStats.lapsed.count}</p>
              <p className="text-xs text-purple-500 font-medium">${filteredDashboardStats.lapsed.premium.toLocaleString()}</p>
            </button>
            <button 
              onClick={() => handleDashboardFilter('notTaken')}
              className="bg-white border-2 border-neutral-300 p-4 rounded-xl hover:border-neutral-500 transition-all duration-200 hover:shadow-md cursor-pointer hover:scale-105 text-left"
            >
              <p className="text-xs font-semibold text-neutral-600 mb-1">Not Taken</p>
              <p className="text-2xl font-bold text-neutral-700">{filteredDashboardStats.notTaken.count}</p>
              <p className="text-xs text-neutral-500 font-medium">${filteredDashboardStats.notTaken.premium.toLocaleString()}</p>
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mt-4">
            <div style={{ width: '40vw' }}>
              <div className="space-y-1">
                <label className="text-xs text-neutral-500 font-medium">Quick Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search by client, policy #, carrier..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-xl"
                  />
                </div>
              </div>
            </div>
            
            {/* Policy Status Multi-Select with Checkboxes */}
            <div style={{ width: '15vw' }}>
              <div className="space-y-1">
                <label className="text-xs text-neutral-500 font-medium">Filter by Policy Status</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="rounded-xl w-full">
                      <Filter className="w-4 h-4 mr-2" />
                      Policy Status {statusFilter.length > 0 && `(${statusFilter.length})`}
                    </Button>
                  </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-2">
                  <div className="font-semibold text-sm mb-2">Select Policy Status</div>
                  {uniqueStatuses.map(status => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={statusFilter.includes(status)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setStatusFilter([...statusFilter, status]);
                          } else {
                            setStatusFilter(statusFilter.filter(s => s !== status));
                          }
                        }}
                      />
                      <label
                        htmlFor={`status-${status}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {status}
                      </label>
                    </div>
                  ))}
                  {statusFilter.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setStatusFilter([])}
                      className="w-full mt-2 text-xs"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Paid Status Multi-Select with Checkboxes */}
            <div style={{ width: '15vw' }}>
              <div className="space-y-1">
                <label className="text-xs text-neutral-500 font-medium">Filter by Paid Status</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="rounded-xl w-full">
                      <Filter className="w-4 h-4 mr-2" />
                      Paid Status {paidStatusFilter.length > 0 && `(${paidStatusFilter.length})`}
                    </Button>
                  </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-2">
                  <div className="font-semibold text-sm mb-2">Select Paid Status</div>
                  {uniquePaidStatuses.map(status => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`paid-${status}`}
                        checked={paidStatusFilter.includes(status)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setPaidStatusFilter([...paidStatusFilter, status]);
                          } else {
                            setPaidStatusFilter(paidStatusFilter.filter(s => s !== status));
                          }
                        }}
                      />
                      <label
                        htmlFor={`paid-${status}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {status}
                      </label>
                    </div>
                  ))}
                  {paidStatusFilter.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPaidStatusFilter([])}
                      className="w-full mt-2 text-xs"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </PopoverContent>
                </Popover>
              </div>
            </div>

            <div style={{ width: '30vw' }}>
              <div className="space-y-1">
                <label className="text-xs text-neutral-500 font-medium">Date Filter</label>
                <div className="flex gap-2">
                  <Select value={dateFilterType} onValueChange={setDateFilterType}>
                    <SelectTrigger className="rounded-xl border-neutral-300 w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="effective">Filter by Effective Date</SelectItem>
                      <SelectItem value="created">Filter by Date Created</SelectItem>
                    </SelectContent>
                  </Select>
                  <DateRangePicker value={dateRange} onChange={setDateRange} />
                </div>
              </div>
            </div>
          </div>

          {/* Clear All Filters Button - Highlighted */}
          {(searchQuery || statusFilter.length > 0 || paidStatusFilter.length > 0 || dateRange) && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter([]);
                setPaidStatusFilter([]);
                setDateRange(null);
              }}
              className="mt-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 w-auto px-4"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Clear All Filters
            </Button>
          )}
        </CardHeader>

        <CardContent>
          {/* Showing Info & Page Size Selector */}
          <div className="flex items-center justify-between text-sm text-neutral-600 mb-4">
            <div className="flex items-center gap-3">
              <span>
                Showing {filteredPolicies.length === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, filteredPolicies.length)} of {filteredPolicies.length} policies
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Rows per page:</span>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {totalPages > 1 && <span>Page {currentPage} of {totalPages}</span>}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto rounded-xl border border-neutral-100">
              {filteredPolicies.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-neutral-600">No policies found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 w-8 first:rounded-tl-xl"></th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Client</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Policy #</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Carrier</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Product</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Effective Date</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Premium</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Status</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Splits</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 last:rounded-tr-xl">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {currentPolicies.map((policy, index) => (
                      <React.Fragment key={policy.id || index}>
                        <tr className="hover:bg-neutral-50 transition-all duration-200">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleRow(policy.id)}
                              className="text-neutral-400 hover:text-[#d4af37]"
                            >
                              {expandedRows[policy.id] ? (
                                <ChevronDown className="w-5 h-5" />
                              ) : (
                                <ChevronRight className="w-5 h-5" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-neutral-800">{policy.client}</p>
                            <p className="text-xs text-neutral-500">{formatDisplayDate(policy.created_at)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-mono text-sm text-neutral-700">{policy.policy_number}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-700">
                            {policy.policyCarrier || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{policy.carrier_product || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-neutral-700">
                              {formatEffectiveDate(policy.initial_draft_date)}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-neutral-800">${policy.annual_premium || 0}</p>
                            <p className="text-xs text-neutral-500">${policy.monthly_premium}/mo</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              {policy.policyStatus && (
                                <Badge className="bg-[#f4e5b8] text-[#b8941e] hover:bg-[#f4e5b8] text-xs w-fit">
                                  {policy.policyStatus}
                                </Badge>
                              )}
                              {policy.isLocked && (
                                <Badge variant="outline" className="border-red-300 text-red-600 text-xs w-fit px-1.5">
                                  <Lock className="w-3 h-3" />
                                </Badge>
                              )}
                              {policy.paid_status && (
                                <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs w-fit">
                                  {policy.paid_status}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {policy._split_of_policy && policy._split_of_policy.length > 0 && (
                              <Badge variant="outline" className="border-[#d4af37] text-[#b8941e]">
                                <Users className="w-3 h-3 mr-1" />
                                {policy._split_of_policy.length}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => handleEdit(policy)}
                                size="sm"
                                disabled={policy.isLocked}
                                className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-lg h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Edit Policy"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {!policy.isLocked && (
                                <Button
                                  onClick={() => handleDelete(policy)}
                                  size="sm"
                                  variant="destructive"
                                  className="rounded-lg h-8 w-8 p-0"
                                  title="Delete Policy"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {expandedRows[policy.id] && (
                          <tr>
                            <td colSpan="10" className="bg-neutral-50 px-4 py-6">
                              <div className="grid grid-cols-4 gap-6 mb-6">
                                {/* Column 1: Connection */}
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-neutral-700 text-sm mb-3 pb-2 border-b border-neutral-300">Connection</h4>
                                  <InfoField label="GHL Contact ID" value={policy.ghl_contactId} />
                                  <InfoField label="Location" value={policy.ghl_locationName} />
                                  <InfoField label="Opportunity" value={policy.ghl_opportunityId} />
                                  <InfoField label="Submitted By" value={policy.submittedBy_name} />
                                  <InfoField label="Created" value={formatDisplayDate(policy.created_at)} />
                                </div>

                                {/* Column 2: Client */}
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-neutral-700 text-sm mb-3 pb-2 border-b border-neutral-300">Client</h4>
                                  <InfoField label="Phone" value={policy.phone} />
                                  <InfoField 
                                    label="Source" 
                                    value={formatWithOther(policy.contactSource, policy.custom_source)} 
                                  />
                                  <InfoField 
                                    label="Type" 
                                    value={formatWithOther(policy.contactType, policy.custom_type)} 
                                  />
                                  <InfoField label="State" value={policy.state} />
                                </div>

                                {/* Column 3: Policy */}
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-neutral-700 text-sm mb-3 pb-2 border-b border-neutral-300">Policy</h4>
                                  <InfoField 
                                    label="Carrier" 
                                    value={formatWithOther(policy.policyCarrier, policy.custom_carrier)} 
                                  />
                                  <InfoField label="Product" value={policy.carrier_product} />
                                  <InfoField label="Recurring Day" value={policy.recurring_draft_day} />
                                  <InfoField label="Annual Premium" value={`$${policy.annual_premium || 0}`} />
                                </div>

                                {/* Column 4: Additional Info */}
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-neutral-700 text-sm mb-3 pb-2 border-b border-neutral-300">Additional Info</h4>
                                  
                                  <div>
                                    <p className="text-xs text-neutral-500 mb-1">Pending Follow-up</p>
                                    {policy.pending_follow_up && policy.pending_follow_up !== 'na' ? (
                                      <>
                                        <p className="text-sm text-neutral-800">
                                          {expandedText[`follow-${policy.id}`] 
                                            ? policy.pending_follow_up 
                                            : truncateText(policy.pending_follow_up, 50)
                                          }
                                        </p>
                                        {policy.pending_follow_up.length > 50 && (
                                          <button
                                            onClick={() => toggleText(`follow-${policy.id}`)}
                                            className="text-xs text-[#d4af37] hover:text-[#b8941e] mt-1"
                                          >
                                            {expandedText[`follow-${policy.id}`] ? 'Show less' : 'Show more'}
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-sm font-medium text-neutral-800">N/A</p>
                                    )}
                                  </div>

                                  <div>
                                    <p className="text-xs text-neutral-500 mb-1">Highlights</p>
                                    {policy.appointment_highlight && policy.appointment_highlight !== 'na' ? (
                                      <>
                                        <p className="text-sm text-neutral-800">
                                          {expandedText[`highlight-${policy.id}`] 
                                            ? policy.appointment_highlight 
                                            : truncateText(policy.appointment_highlight, 50)
                                          }
                                        </p>
                                        {policy.appointment_highlight.length > 50 && (
                                          <button
                                            onClick={() => toggleText(`highlight-${policy.id}`)}
                                            className="text-xs text-[#d4af37] hover:text-[#b8941e] mt-1"
                                          >
                                            {expandedText[`highlight-${policy.id}`] ? 'Show less' : 'Show more'}
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-sm font-medium text-neutral-800">N/A</p>
                                    )}
                                  </div>

                                  <InfoField label="Beneficiary" value={policy.beneficiary} />
                                </div>
                              </div>

                              {/* Splits Section */}
                              <div className="pt-4 border-t border-neutral-300">
                                <button
                                  onClick={() => toggleSplits(policy.id)}
                                  className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-[#d4af37] transition-colors mb-3"
                                >
                                  {expandedSplits[policy.id] ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                  <Users className="w-4 h-4" />
                                  Policy Splits ({policy._split_of_policy?.length || 0})
                                </button>
                                
                                {expandedSplits[policy.id] && (
                                  <SplitsManager
                                    policy={policy}
                                    onSave={fetchPolicies}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="rounded-lg"
              >
                Previous
              </Button>
              <span className="text-sm text-neutral-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="rounded-lg"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {showEditDialog && editingPolicy && (
        <EditPolicyDialog
          policy={editingPolicy}
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          onSave={fetchPolicies}
          userInfo={userInfo}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteConfirmModal(false);
          setDeletingPolicy(null);
          setDeleteReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this policy? This action is irreversible and cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deletingPolicy && (
            <div className="space-y-4 py-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-xs font-semibold text-red-700 mb-3">Policy Details</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Client:</span>
                    <span className="font-medium text-neutral-800">
                      {deletingPolicy.client || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Policy #:</span>
                    <span className="font-mono font-medium text-neutral-800">
                      {deletingPolicy.policy_number || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Carrier:</span>
                    <span className="font-medium text-neutral-800">
                      {deletingPolicy.policyCarrier || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Annual Premium:</span>
                    <span className="font-semibold text-red-600">
                      ${parseFloat(deletingPolicy.annual_premium || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-neutral-700">
                  Reason for Deletion <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Please provide a reason for deleting this policy..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="min-h-[100px] resize-none"
                  disabled={deleting}
                />
                {!deleteReason.trim() && (
                  <p className="text-xs text-neutral-500">This field is required</p>
                )}
              </div>

              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-800 font-medium">
                  ⚠️ Warning: This will permanently delete the policy from the system.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setDeletingPolicy(null);
                setDeleteReason('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting || !deleteReason.trim()}
              variant="destructive"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper component for displaying field info
function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-neutral-500 mb-1">{label}</p>
      <p className="text-sm font-medium text-neutral-800">{value || 'N/A'}</p>
    </div>
  );
}
